// React
import React, { useEffect, useState, useCallback } from 'react';

// Assets
import { DeleteFilled } from '@ant-design/icons';

// Components
import IconButton from 'components/@extended/IconButton';

// Packages
import { Loader } from 'components';
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    InputLabel,
    Stack,
    TextField,
    Tooltip,
    FormControlLabel,
    Switch
} from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';
import 'yup-phone-lite';

// Styles
import 'react-widgets/scss/styles.scss';

// Utils
import { errorMessage, nameRegExp } from 'utils/constants';
import { capitalizeFirstLetterMulti } from 'utils/Helpers';
import CountryCodeSelect from '../common/CountryCodeSelect';

const RequestDetailsModal = ({
    onClose,
    selectedRegister,
    properties,
    loading,
    onUpdate,
    onDelete
}) => {
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const submit = values => {
        const formattedPhone = phone => {
            return phone.replace(/[^0-9]/g, '').length > 10
                ? `${phone
                      .replace(/[^0-9]/g, '')
                      .substring(phone.replace(/[^0-9]/g, '').length - 10)}`
                : `${phone.replace(/[^0-9]/g, '')}`;
        };

        const property_id = selectedProperty?.id || selectedRegister.property_id;
        const fullAddress =
            selectedProperty?.full_address || selectedProperty?.address || values.address;

        const {
            email,
            first_name,
            last_name,
            phone,
            code,
            country,
            calls,
            invite,
            primary,
            minor
        } = values;

        const trimmedEmail = email?.trim().toLowerCase();

        const register = {
            address: fullAddress,
            first_name: first_name.trim().toLowerCase(),
            id: selectedRegister.id,
            devices: selectedRegister?.devices || null,
            last_name: last_name.trim().toLowerCase(),
            permissions: { calls, invite },
            phone: {
                number: Number.parseInt(formattedPhone(phone.toString())),
                code: Number.parseInt(code.toString()),
                country
            },
            property_id,
            registered_at: selectedRegister.registered_at,
            type: selectedRegister.type,
            primary,
            minor
        };

        if (trimmedEmail) {
            register.email = trimmedEmail;
        }

        onUpdate(register);
    };

    const getSelectedProperty = useCallback(
        id => {
            const property = properties.filter(prop => prop.id === id)[0];
            setSelectedProperty(property);
        },
        [properties]
    );

    useEffect(() => {
        if (selectedRegister && !selectedProperty) {
            getSelectedProperty(selectedRegister.property_id);
        }
    }, [selectedProperty, selectedRegister, getSelectedProperty]);

    const initialValues = {
        address: capitalizeFirstLetterMulti(
            selectedProperty?.address || selectedRegister?.address || ''
        ),
        email: selectedRegister?.email ? selectedRegister.email.trim() : '',
        first_name: selectedRegister?.first_name?.trim() || '',
        last_name: selectedRegister?.last_name?.trim() || '',
        phone: selectedRegister?.phone?.number ? `${selectedRegister.phone.number}` : '',
        code: selectedRegister?.phone?.code ? `${selectedRegister.phone.code}` : '1',
        country: selectedRegister?.phone?.country
            ? `${selectedRegister.phone.country}`
            : 'US',
        calls: selectedRegister?.permissions?.includes('calls') || false,
        invite: selectedRegister?.permissions?.includes('invite') || false,
        primary: selectedRegister?.primary || false,
        minor: selectedRegister?.minor || false
    };

    const validationSchema = Yup.object().shape({
        address: Yup.string().required(errorMessage.address.required),
        first_name: Yup.string()
            .trim()
            .matches(nameRegExp.format, errorMessage.firstName.valid)
            .required(errorMessage.firstName.required),
        last_name: Yup.string()
            .trim()
            .matches(nameRegExp.format, errorMessage.lastName.valid)
            .required(errorMessage.lastName.required),
        email: Yup.string().email(errorMessage.email.valid),
        phone: Yup.string().trim().required(errorMessage.mobilePhone.required),
        country: Yup.string().trim(),
        code: Yup.string().trim(),
        calls: Yup.boolean(),
        invite: Yup.boolean(),
        primary: Yup.boolean(),
        minor: Yup.boolean()
    });

    const validatePhoneEmail = ({ phone, code, country }) => {
        // Duplicate (phone/email already on the property) validation is
        // intentionally NOT done here. On an EDIT it produced false positives —
        // the invite matched its own phone/email and blocked name-only edits —
        // and duplicate-prevention is the backend's responsibility. We keep
        // only client-side phone-format validation.
        const validatePhoneNumber = () => {
            if (!phone) return true;

            const phoneSchema = Yup.string().phone(country);

            try {
                phoneSchema.validateSync(`${code}${phone}`);
                return true;
            } catch {
                return false;
            }
        };

        if (!validatePhoneNumber()) {
            return {
                phone: 'Mobile phone needs to be a valid number'
            };
        }
    };

    const handleRemove = () => {
        onDelete(selectedRegister);
    };

    if (!selectedRegister || !selectedProperty) return <Loader />;

    return (
        <>
            <DialogTitle>{`Edit Member Invite`}</DialogTitle>
            <Divider />
            <Formik
                initialValues={initialValues}
                onSubmit={submit}
                validate={validatePhoneEmail}
                validationSchema={validationSchema}
                validateOnChange
            >
                {({
                    errors,
                    handleBlur,
                    handleChange,
                    handleSubmit,
                    validateField,
                    setFieldValue,
                    touched,
                    values
                }) => (
                    <>
                        <DialogContent sx={{ p: 2.5, maxHeight: '70vh' }}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={12}>
                                    <Stack spacing={0.5}>
                                        <InputLabel>Street Address</InputLabel>
                                        <Autocomplete
                                            id="address"
                                            name="address"
                                            options={Object.values(properties || {})}
                                            getOptionLabel={option =>
                                                capitalizeFirstLetterMulti(
                                                    option.address || ''
                                                )
                                            }
                                            defaultValue={selectedProperty}
                                            onChange={(_, newValue) => {
                                                if (newValue) {
                                                    handleChange({
                                                        target: {
                                                            name: 'address',
                                                            value: capitalizeFirstLetterMulti(
                                                                newValue.address || ''
                                                            )
                                                        }
                                                    });
                                                    setSelectedProperty(newValue);
                                                }
                                            }}
                                            onBlur={handleBlur}
                                            renderInput={params => (
                                                <TextField
                                                    {...params}
                                                    required
                                                    placeholder="1234 Main St"
                                                    fullWidth
                                                    autoComplete="shipping address-line1"
                                                    error={
                                                        touched.address &&
                                                        !!errors.address
                                                    }
                                                    helperText={
                                                        touched.address && errors.address
                                                    }
                                                    value={values.address}
                                                />
                                            )}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Stack spacing={0.5}>
                                        <InputLabel>First Name</InputLabel>
                                        <TextField
                                            required
                                            id="firstNameBasic"
                                            name="first_name"
                                            placeholder="First Name"
                                            fullWidth
                                            autoComplete="given-name"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={
                                                touched.first_name && !!errors.first_name
                                            }
                                            helperText={
                                                touched.first_name && errors.first_name
                                            }
                                            value={values.first_name}
                                            inputProps={{
                                                style: { textTransform: 'capitalize' }
                                            }}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Stack spacing={0.5}>
                                        <InputLabel>Last Name</InputLabel>
                                        <TextField
                                            required
                                            id="lastNameBasic"
                                            name="last_name"
                                            placeholder="Last name"
                                            fullWidth
                                            autoComplete="family-name"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={
                                                touched.last_name && !!errors.last_name
                                            }
                                            helperText={
                                                touched.last_name && errors.last_name
                                            }
                                            value={values.last_name}
                                            inputProps={{
                                                style: { textTransform: 'capitalize' }
                                            }}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Stack spacing={0.5}>
                                        <InputLabel>Email</InputLabel>
                                        <TextField
                                            required
                                            id="countryBasic"
                                            name="email"
                                            placeholder="Email"
                                            fullWidth
                                            autoComplete="email"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.email && !!errors.email}
                                            helperText={touched.email && errors.email}
                                            value={values.email}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={4} sm={2}>
                                    <Stack spacing={0.5}>
                                        <InputLabel htmlFor="phone">
                                            Country Code
                                        </InputLabel>
                                        <CountryCodeSelect
                                            label=""
                                            size="medium"
                                            value={selectedCountry}
                                            defaultRegion={values.country || 'US'}
                                            onChange={newValue => {
                                                if (!newValue) return;

                                                setSelectedCountry(newValue);
                                                setFieldValue(
                                                    'code',
                                                    String(newValue.code).replace(
                                                        /\D/g,
                                                        ''
                                                    )
                                                );
                                                setFieldValue(
                                                    'country',
                                                    String(newValue.region)
                                                );

                                                setTimeout(() => validateField('phone'));
                                            }}
                                            sx={{ width: '100%' }}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={8} sm={4}>
                                    <Stack spacing={0.5}>
                                        <InputLabel>Mobile Phone</InputLabel>
                                        <TextField
                                            required
                                            id="phoneNumberBasic"
                                            name="phone"
                                            inputMode="numeric"
                                            onChange={e =>
                                                setFieldValue(
                                                    'phone',
                                                    e.target.value.replace(/\D/g, '')
                                                )
                                            }
                                            placeholder="Phone"
                                            fullWidth
                                            autoComplete="given-name"
                                            onBlur={handleBlur}
                                            error={touched.phone && !!errors.phone}
                                            helperText={touched.phone && errors.phone}
                                            value={values.phone}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={6} sm={6}>
                                    <Stack spacing={2} marginTop={3}>
                                        <Stack direction="row" spacing={4}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={values.primary}
                                                        onChange={(_, checked) => {
                                                            setFieldValue(
                                                                'primary',
                                                                checked
                                                            );

                                                            if (checked) {
                                                                setFieldValue(
                                                                    'minor',
                                                                    false
                                                                );
                                                                setFieldValue(
                                                                    'calls',
                                                                    false
                                                                );
                                                                setFieldValue(
                                                                    'invite',
                                                                    false
                                                                );
                                                            }
                                                        }}
                                                        disabled={values.minor}
                                                    />
                                                }
                                                label="Owner"
                                            />

                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={values.minor}
                                                        onChange={(_, checked) => {
                                                            setFieldValue(
                                                                'minor',
                                                                checked
                                                            );

                                                            if (checked) {
                                                                setFieldValue(
                                                                    'primary',
                                                                    false
                                                                );
                                                                setFieldValue(
                                                                    'calls',
                                                                    true
                                                                );
                                                                setFieldValue(
                                                                    'invite',
                                                                    true
                                                                );
                                                            } else {
                                                                setFieldValue(
                                                                    'calls',
                                                                    false
                                                                );
                                                                setFieldValue(
                                                                    'invite',
                                                                    false
                                                                );
                                                            }
                                                        }}
                                                        disabled={values.primary}
                                                    />
                                                }
                                                label="Minor"
                                            />
                                        </Stack>
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Stack spacing={0.5}>
                                        <InputLabel>Permissions</InputLabel>
                                        <Box paddingTop={0.5}>
                                            <FormControlLabel
                                                value="end"
                                                control={
                                                    <Checkbox
                                                        checked={values.calls}
                                                        disabled={values.minor}
                                                        onChange={() =>
                                                            setFieldValue(
                                                                'calls',
                                                                !values.calls
                                                            )
                                                        }
                                                    />
                                                }
                                                label="Calls"
                                                labelPlacement="end"
                                                sx={{ ml: 1 }}
                                            />
                                            <FormControlLabel
                                                value="end"
                                                control={
                                                    <Checkbox
                                                        checked={values.invite}
                                                        disabled={values.minor}
                                                        onChange={() =>
                                                            setFieldValue(
                                                                'invite',
                                                                !values.invite
                                                            )
                                                        }
                                                    />
                                                }
                                                label="Invite"
                                                labelPlacement="end"
                                                sx={{ ml: 1 }}
                                            />
                                        </Box>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <Divider />
                        <DialogActions sx={{ p: 2.5 }}>
                            <Grid
                                container
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Grid item>
                                    <Tooltip title="Delete Registration" placement="top">
                                        <IconButton
                                            size="large"
                                            color="error"
                                            onClick={handleRemove}
                                            disabled={loading}
                                        >
                                            <DeleteFilled />
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                                <Grid item>
                                    <Stack
                                        direction="row"
                                        spacing={2}
                                        alignItems="center"
                                    >
                                        <Button
                                            color="error"
                                            onClick={onClose}
                                            disabled={loading}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            onClick={handleSubmit}
                                            disabled={loading}
                                        >
                                            Update
                                        </Button>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </DialogActions>
                    </>
                )}
            </Formik>
        </>
    );
};

export default RequestDetailsModal;
